import { useEffect } from 'react';
import { Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom';
import { Home, Dumbbell, UtensilsCrossed, MessageCircle, TrendingUp, Settings as SettingsIcon } from 'lucide-react';

import { useSession } from './lib/store.js';
import { Toast, Spinner, Card, Button } from './components/ui.jsx';

import Onboarding from './pages/Onboarding.jsx';
import Today from './pages/Today.jsx';
import Train from './pages/Train.jsx';
import Session from './pages/Session.jsx';
import Nutrition from './pages/Nutrition.jsx';
import MealBank from './pages/MealBank.jsx';
import Progress from './pages/Progress.jsx';
import Review from './pages/Review.jsx';
import Settings from './pages/Settings.jsx';
import Coach from './pages/Coach.jsx';
import Migraines from './pages/Migraines.jsx';
import Safety from './pages/Safety.jsx';

const TABS = [
  { to: '/', label: 'Today', icon: Home, end: true },
  { to: '/train', label: 'Train', icon: Dumbbell },
  { to: '/food', label: 'Food', icon: UtensilsCrossed },
  { to: '/coach', label: 'Coach', icon: MessageCircle },
  { to: '/progress', label: 'Progress', icon: TrendingUp },
  { to: '/settings', label: 'You', icon: SettingsIcon },
];

export default function App() {
  const { status, bootstrap, error } = useSession();
  const location = useLocation();

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (status === 'loading') return <Spinner label="Opening" />;
  if (status === 'offline') {
    return (
      <div className="min-h-screen flex items-center justify-center px-gutter">
        <Card className="max-w-sm w-full text-center">
          <h1 className="text-card-title font-display mb-2">Can&rsquo;t reach your programme</h1>
          <p className="text-body-sm text-textSoft mb-4">{error || 'Something went wrong loading your data.'}</p>
          <Button onClick={() => bootstrap()} className="w-full">Try again</Button>
        </Card>
      </div>
    );
  }
  if (status === 'onboarding') return <Onboarding />;

  // The session player takes the whole screen — no tab bar to tap by accident
  // mid-set, and nothing competing with the exercise in front of her.
  const isSession = /^\/train\/\d+\/\d+/.test(location.pathname);

  return (
    <div className="min-h-screen bg-bg">
      <main className={isSession ? '' : 'safe-bottom'}>
        <Routes>
          <Route path="/" element={<Today />} />
          <Route path="/train" element={<Train />} />
          <Route path="/train/:week/:slot" element={<Session />} />
          <Route path="/food" element={<Nutrition />} />
          <Route path="/food/bank" element={<MealBank />} />
          <Route path="/coach" element={<Coach />} />
          <Route path="/migraines" element={<Migraines />} />
          <Route path="/progress" element={<Progress />} />
          <Route path="/review" element={<Review />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/safety" element={<Safety />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {!isSession && <TabBar />}
      <Toast />
    </div>
  );
}

function TabBar() {
  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-40 bg-panel/95 backdrop-blur border-t border-edge"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      aria-label="Main"
    >
      <ul className="flex">
        {TABS.map(({ to, label, icon: Icon, end }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 px-0.5 transition-colors ${
                  isActive ? 'text-accent' : 'text-textMute hover:text-textSoft'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={19} strokeWidth={isActive ? 2.2 : 1.7} />
                  {/* Six tabs on a 375px screen: 10px keeps "Progress" on one line. */}
                  <span className="text-[10px] font-semibold uppercase tracking-tight">{label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
